"use client";

import { useState } from "react";
import Image from "next/image";
import { Icon } from "./icon";
import { ProfilePagination } from "@/components/profile/profile-pagination";
import { formatPriceCompact, formatInt } from "@/lib/format";
import { TINTS } from "./chip-group";
import { CARD_SHELL_STYLE, PHOTO_SCRIM } from "./profile-card-style";
import type { ProfileReview, ProfileReviewSummary } from "./profile-types";
import type { VisibleRating } from "@/lib/seo/review-rating";

/**
 * Public agent profile "Reviews" section — the v1 re-skin sand panel
 * (spec §8.3.8). CLIENT component: it owns the page state for the 6-per-page
 * review grid (`ProfilePagination`) and each card's read-more toggle. Props
 * are serializable (a reviews array + a plain summary object), so the server
 * page renders it directly.
 *
 * This section remains the existing non-Google review surface. Its aggregate
 * is also the basis for structured data, profile JSON, and AI. Official Google
 * reviews render separately in `GoogleReviewsSection` and only join this
 * aggregate in the human-visible card/profile-hero rating.
 *
 * Three stacked blocks inside one full-width sand-tinted panel:
 *  1. The AI Review Summary white card (`ad_agent_profile.review_ai_summary`)
 *     — sun icon tile, "AI SUMMARY" label, a tinted sentiment badge, the
 *     summary prose, and the themes as sun-tinted chips. Rendered only when
 *     there is prose or at least one theme (sentiment alone isn't enough to
 *     fill a card). Regenerated nightly (Phase 35).
 *  2. A 2-up grid of review cards (`ad_reviews` where `is_hidden = false`),
 *     paginated client-side, 6 per page. Two variants: a sold-listing card
 *     (snapshot photo + SOLD pill + price/address scrim + specs row) and a
 *     plain quote card. Long quotes (> 220 chars — a deterministic threshold,
 *     hydration-safe vs. measuring clamped heights) collapse behind a
 *     "Read more" toggle.
 *  3. No reviews AND no summary content: omit the section and its heading.
 *
 * The snapshot is a plain JSONB object (`AdSoldListingSnapshot`); its fields
 * are all optional, so every row renders defensively (omit when absent).
 * Snapshot photos are SCRAPED review payloads (Zillow/Google/… — an unbounded
 * host set), so they render via `next/image` with `unoptimized`: an
 * off-config host through the optimizer throws at render, and these hosts can
 * never all be enrolled in remotePatterns. Dates format via a module-level
 * formatter pinned to `'en-US'` so SSR and the client agree (hydration safety
 * — repo lesson 2026-04-23).
 */

const DATE_FMT = new Intl.DateTimeFormat("en-US", {
  year: "numeric",
  month: "long",
  day: "numeric",
});

/** Reviews per page (2-col grid → 3 rows). */
const PAGE_SIZE = 6;

/** Deterministic read-more cutoff (chars) — no client-side height measuring. */
const READ_MORE_THRESHOLD = 220;

/**
 * Attribution shown when a review carries no reviewer name (TRU-72).
 *
 * A DISPLAY fallback, never a persisted value: sources routinely publish
 * reviews without a name, and `reviewer_name` must stay NULL in the database so
 * a later scrape that does supply one still fills it in. It is also why the
 * scrape path no longer invents placeholder names of its own.
 */
export const ANONYMOUS_REVIEWER = "Anonymous";

/** Format an ISO date string → "Month D, YYYY", or null if unparseable/absent. */
function formatReviewDate(iso: string | null): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return DATE_FMT.format(date);
}

/**
 * Sentiment badge tints — the shared chip TINTS recipes: success for
 * excellent/very positive, sky for positive, sun for mixed, neutral for
 * anything else/unknown.
 */
function sentimentBadgeStyle(sentiment: string): React.CSSProperties {
  const s = sentiment.trim().toLowerCase();
  const tint =
    s === "excellent" || s === "very positive"
      ? TINTS.success
      : s === "positive"
        ? TINTS.sky
        : s === "mixed"
          ? TINTS.sun
          : TINTS.neutral;
  return { backgroundColor: tint.background, color: tint.color };
}

/** A row of filled star glyphs sized to `rating` (clamped 0–5). */
function StarRating({ rating }: { rating: number }) {
  const count = Math.max(0, Math.min(5, Math.round(rating)));
  if (count === 0) return null;
  return (
    <span
      className="inline-flex items-center"
      role="img"
      aria-label={`${count} out of 5 stars`}
    >
      {Array.from({ length: count }).map((_, i) => (
        <Icon
          key={i}
          name="star"
          size={16}
          filled
          style={{ color: "var(--brand-sun)" }}
        />
      ))}
    </span>
  );
}

/**
 * Shared review body — stars + role pill header, the quoted text with the
 * deterministic read-more toggle, the attribution line, and the muted
 * source/date footer. Own component so each card carries its own
 * expand/collapse state.
 */
function ReviewBody({ review }: { review: ProfileReview }) {
  const [expanded, setExpanded] = useState(false);
  const text = review.text?.trim() ?? "";
  const isLong = text.length > READ_MORE_THRESHOLD;
  const display =
    expanded || !isLong
      ? text
      : `${text.slice(0, READ_MORE_THRESHOLD).trimEnd()}…`;
  // A review with no usable reviewer name is attributed as "Anonymous" rather
  // than silently losing its attribution line (TRU-72). DISPLAY-ONLY: the
  // fallback is never written back, so `reviewer_name` stays NULL and a later
  // scrape that does supply a real name still fills it in.
  const reviewerName = review.reviewerName?.trim() || ANONYMOUS_REVIEWER;
  const formattedDate = formatReviewDate(review.reviewDate);

  return (
    <div>
      {(review.rating !== null || review.reviewerRole) && (
        <div className="mb-2 flex items-center justify-between gap-2">
          {review.rating !== null ? (
            <StarRating rating={review.rating} />
          ) : (
            <span aria-hidden="true" />
          )}
          {review.reviewerRole && (
            <span
              className="rounded-full px-2 py-0.5 text-[11px] font-semibold capitalize"
              style={{
                backgroundColor: "var(--brand-surface-subtle)",
                color: "var(--brand-text-muted)",
              }}
            >
              {review.reviewerRole}
            </span>
          )}
        </div>
      )}

      {text && (
        <>
          <p
            className="text-[14px] italic leading-relaxed"
            style={{ color: "var(--brand-text)" }}
          >
            &ldquo;{display}&rdquo;
          </p>
          {isLong && (
            <button
              type="button"
              onClick={() => setExpanded((e) => !e)}
              className="mt-1 text-[13px] font-medium"
              style={{ color: "var(--brand-primary-text, var(--brand-primary))" }}
            >
              {expanded ? "Show less" : "Read more"}
            </button>
          )}
        </>
      )}

      <p
        className="mt-3 text-[14px] font-semibold"
        style={{ color: "var(--brand-text)" }}
      >
        &mdash; {reviewerName}
      </p>

      {/* Tiny muted footer — per-review source label + date (when present). */}
      {(
        <p
          className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px]"
          style={{ color: "var(--brand-text-muted)" }}
        >
          <span>Source: External</span>
          {formattedDate && <span>&middot;</span>}
          {formattedDate && <span>{formattedDate}</span>}
        </p>
      )}
    </div>
  );
}

/**
 * One review card. VARIANT A (sold-listing snapshot attached): photo block
 * with SOLD pill + price/address scrim, then a specs row, then the body.
 * Snapshot present but `photoUrl` absent → no photo block, specs + body keep.
 * VARIANT B (no snapshot): just the body.
 */
function ReviewCard({ review }: { review: ProfileReview }) {
  const snapshot = review.soldListingSnapshot;

  if (!snapshot) {
    return (
      <li className="p-5" style={CARD_SHELL_STYLE}>
        <ReviewBody review={review} />
      </li>
    );
  }

  const hasSpecs =
    snapshot.beds != null || snapshot.baths != null || snapshot.sqft != null;

  return (
    <li className="overflow-hidden" style={CARD_SHELL_STYLE}>
      {snapshot.photoUrl && (
        <div className="relative h-40 overflow-hidden">
          {/* `unoptimized`: scraped snapshot photos live on an unbounded host
              set — never route an arbitrary host through the optimizer. */}
          <Image
            src={snapshot.photoUrl}
            alt={snapshot.address || "Sold property"}
            fill
            unoptimized
            className="object-cover"
          />
          <span
            className="absolute left-3 top-3 rounded-full px-2.5 py-1 text-[11px] font-bold uppercase"
            style={{
              backgroundColor: "var(--brand-success)",
              color: "var(--brand-primary-foreground)",
            }}
          >
            Sold
          </span>
          {/* Bottom scrim — price + address over a text-tint gradient. */}
          <div
            className="absolute inset-x-0 bottom-0 px-4 pb-3 pt-10"
            style={{
              backgroundImage: PHOTO_SCRIM,
            }}
          >
            {typeof snapshot.price === "number" && (
              <p
                className="text-xl font-bold"
                style={{ color: "var(--brand-primary-foreground)" }}
              >
                {formatPriceCompact(snapshot.price)}
              </p>
            )}
            {snapshot.address && (
              <p
                className="truncate text-[12.5px]"
                style={{
                  color:
                    "color-mix(in srgb, var(--brand-primary-foreground) 80%, transparent)",
                }}
              >
                {snapshot.address}
              </p>
            )}
          </div>
        </div>
      )}

      <div className="p-4">
        {hasSpecs && (
          <div
            className="mb-3 flex items-center gap-3 border-b pb-3 text-[12.5px]"
            style={{
              color: "var(--brand-text-muted)",
              borderColor:
                "color-mix(in srgb, var(--brand-text) 12%, transparent)",
            }}
          >
            {snapshot.beds != null && (
              <span className="flex items-center gap-1">
                <Icon name="bed" size={14} />
                {snapshot.beds} bd
              </span>
            )}
            {snapshot.baths != null && (
              <span className="flex items-center gap-1">
                <Icon name="bathtub" size={14} />
                {snapshot.baths} ba
              </span>
            )}
            {snapshot.sqft != null && (
              <span className="flex items-center gap-1">
                <Icon name="square_foot" size={14} />
                {formatInt(snapshot.sqft)} sf
              </span>
            )}
          </div>
        )}
        <ReviewBody review={review} />
      </div>
    </li>
  );
}

export function ReviewsSection({
  reviews,
  summary,
  rating = null,
}: {
  reviews: ProfileReview[];
  summary: ProfileReviewSummary | null;
  /**
   * Average of the RATED VISIBLE reviews, from the single `computeVisibleRating`
   * owner — the same basis used by JSON-LD, profile JSON and AI summary inputs.
   * `null` ⇒ render nothing beside the heading
   * (never "0.0", never an empty star row).
   */
  rating?: VisibleRating | null;
}) {
  const [page, setPage] = useState(1);

  // The AI card needs prose or themes — sentiment alone can't fill a card.
  const summaryCard =
    summary && (summary.summary?.trim() || summary.themes.length > 0)
      ? summary
      : null;
  const totalPages = Math.ceil(reviews.length / PAGE_SIZE);
  const pageReviews = reviews.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  if (!reviews.length && !summaryCard) return null;

  return (
    <section
      aria-label="External Reviews"
      className="p-6 sm:p-8"
      style={{
        backgroundColor: "var(--brand-sand)",
        border:
          "1px solid color-mix(in srgb, var(--brand-sun) 22%, transparent)",
        borderRadius: "var(--radius-card, 8px)",
      }}
    >
      <div className="mb-5 flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <h2
          className="text-xl font-semibold"
          style={{
            color: "var(--brand-text)",
            fontFamily: "var(--brand-font-heading)",
          }}
        >
          External Reviews
        </h2>

        {rating && (
          <span
            className="inline-flex items-baseline gap-1.5 text-[15px]"
            style={{ color: "var(--brand-text)" }}
          >
            <Icon
              name="star"
              size={16}
              filled
              style={{ color: "var(--brand-sun)", alignSelf: "center" }}
              aria-hidden
            />
            <span className="font-semibold">
              {rating.ratingValue.toFixed(1)}
            </span>
            <span style={{ color: "var(--brand-text-muted)" }}>
              ({rating.reviewCount}
              {rating.reviewCount === 1 ? " review" : " reviews"})
            </span>
          </span>
        )}
      </div>

      {/* 1. AI Review Summary — only when there is prose or themes (Phase 35). */}
      {summaryCard && (
        <div
          aria-label="AI review summary"
          className="mb-6 p-5 sm:p-6"
          style={{
            backgroundColor: "var(--brand-card-bg)",
            border:
              "1px solid color-mix(in srgb, var(--brand-sun) 30%, transparent)",
            borderRadius: "var(--radius-card, 8px)",
          }}
        >
          <div className="flex flex-wrap items-center gap-3">
            <span
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg"
              style={{ backgroundColor: "var(--brand-sun)" }}
            >
              <Icon
                name="wand_stars"
                size={20}
                style={{ color: "var(--brand-primary-foreground)" }}
              />
            </span>
            <span
              className="text-[12px] font-bold uppercase tracking-[0.08em]"
              style={{
                color:
                  "color-mix(in srgb, var(--brand-sun) 55%, var(--brand-text))",
              }}
            >
              AI Summary
            </span>
            {summaryCard.sentiment && summaryCard.sentiment.trim() && (
              <span
                className="rounded-full px-2.5 py-0.5 text-[11px] font-bold uppercase"
                style={sentimentBadgeStyle(summaryCard.sentiment)}
              >
                {summaryCard.sentiment.toUpperCase()}
              </span>
            )}
          </div>

          {summaryCard.summary && summaryCard.summary.trim() && (
            <p
              className="mt-3 text-[14.5px] leading-relaxed"
              style={{ color: "var(--brand-text)" }}
            >
              {summaryCard.summary}
            </p>
          )}

          {summaryCard.themes.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2">
              {summaryCard.themes.map((theme) => (
                <span
                  key={theme}
                  className="rounded-full px-3 py-1 text-[12.5px] font-medium"
                  style={{
                    backgroundColor: TINTS.sun.background,
                    color: TINTS.sun.color,
                  }}
                >
                  {theme}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* 2. Review cards — 2-up grid, 6 per page. */}
      {reviews.length > 0 && (
        <>
          <ul className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            {pageReviews.map((review) => (
              <ReviewCard key={review.id} review={review} />
            ))}
          </ul>
          <ProfilePagination
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
            ariaLabel="Review pages"
          />
        </>
      )}
    </section>
  );
}
